// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Class for generic polygon, to be extended by other polygon classes.
 * @author James Reid
 */

// @ts-check

// @@imports-module
import { Edge } from "./Edge.js"
import { Vector2D } from "./Vector.js"

// @@imports-utils
import { DoubleLinkedList, DoubleLinkedListNode } from "../../data/index.js"
import { DecoratedError } from "../../logging/index.js"

// @@imports-types
import { DoubleLinkedNode } from "../../../types/LinkedNode.js"

// @@body
/**
 * Base class for building polygons consisting of vertex and edge properties.
 * Specific shapes or types of shape must extend this class and implement the
 * required methods to add/remove vertices etc. according to the limitations of
 * the given shape. Methods provided in this base class are universal to all
 * polygons. Implementation provides:
 * - Add and remove vertices following basic restrictions (these methods may be
 * overridden by extended classes).
 * - Manipulate edges by removing not required vertices, splitting at a certain
 * point etc.
 * - Methods to transform (rotate, translate, copy etc.) the polygon.
 * - Fetch angles at a given vertex.
 * @todo Implement basic add vertex method including adding vertex at a given
 * point (this functionality should *not* be available in rectilinear class).
 * @todo Implement basic move edge method which can move edge in any direction,
 * and only checks for intersections.
 * @todo Implement universal border calculations including border radius which
 * can handle any border width and angle at each vertex.
 * @todo Implement calculation memory of area property, bearing in mind that the
 * calculation must be reset whenever a vertex or edge is changed. Calculation
 * must also be reset as required from any instance of an extended class.
 * @todo Implement calculation memory of visual center of polygon used in
 * rotation method, again bearing in mind that the calculation must be reset
 * whenever a vertex or edge is changed.
 * @todo Resolve subtract parallel edge single orphaned vertex bug (see private
 * subtractParallelEdgeFromTarget method for more).
 * @todo Return updated shapes in transforms where inPlace is false.
 * @todo Update #subtractParallelEdgeFromTarget method to split edges based on
 * split vertex instead of split fraction in order to prevent float errors.
 */
class Polygon {
    vertices = /** @type {DoubleLinkedList.<Vector2D>} */
        (new DoubleLinkedList())
    edges = /** @type {DoubleLinkedList.<Edge>} */ (new DoubleLinkedList())
    #isLocked = false
    #rotation = 0

    /**
     * Build empty polygon with default starting vertex in linked list of
     * vertices, and no default edges.
     */
    constructor() {}

    /**
     * Add a new vertex in sequence to the end the current instance's array of
     * vertices. Will also add a new edge required to join this new vertex to
     * the existing curve.
     * @param {Vector2D} vertex Vertex to add to current polygon instance.
     * @throws {PolygonError} Throws when:
     * - New edge intersects any other existing edge except at the end vertex of
     * the previous edge.
     */
    // eslint-disable-next-line no-unused-vars -- Not yet implemented.
    addVertex(vertex) {

    }

    /**
     * Remove vertex from current polygon instance, also removing all vertices
     * after this vertex, and all connecting edges.
     * @param {Vector2D} vertex Vertex to remove from current polygon instance.
     * @throws {PolygonError} Throws when:
     * - Vertex node with given value is not found in internal vertex linked
     * list.
     */
    removeVertex(vertex) {
        // Fetch vertex index by node value. Throw if index does not resolve to
        // a positive number (including 0).
        const index = this.vertices.indexOfValue(vertex)
        if (index <= 0) {
            throw new PolygonError({
                message: "Vertex node with value not found in polygon",
                fix: "Remove a vertex which exists in polygon"
            })
        }

        this.vertices.removeNode(index, { recursive: true })
        this.edges.removeNode(index - 1, { recursive: true })
    }

    /**
     * Remove any vertices where both connected *edges* go in the same
     * direction, and replace both edges with one, longer edge.
     * @returns {void} Recurses as required when removing 0-magnitude edges.
     */
    dissolveVertices() {
        for (const edgeNode of this.edges) {
            // Fetch current and next edge, continuing if next edge doesn't
            // exist since in this case no vertices are redundant.
            const { value: edge, next: nextEdgeNode } = edgeNode
            if (!nextEdgeNode) { continue }
            const { value: nextEdge } = nextEdgeNode

            // If edge has no magnitude, remove it.
            if (!edge.magnitude) {
                // Remove *current* edge and *current* vertex only. No further
                // updates are required since this edge has zero magnitude.
                this.edges.removeNode(edgeNode)
                const vertexNode = this.vertices.findNode(node => {
                    return node.value.equals(edge.start)
                })
                if (vertexNode) { this.vertices.removeNode(vertexNode) }

                // Recurse required to check if edges either side of edge just
                // removed are in same direction.
                return this.dissolveVertices()
            }

            // If edge is in the same direction as the following edge, remove
            // the middle vertex which connects both edges, remove the following
            // edge, and update the current edge length as required.
            if (edge.direction === nextEdge.direction) {
                // Remove *next* edge and *next* vertex (i.e. the next edge, and
                // the *end* node of the current edge).
                this.edges.removeNode(nextEdgeNode)
                const vertexNode = this.vertices.findNode(node => {
                    return node.value.equals(edge.end)
                })
                if (vertexNode) { this.vertices.removeNode(vertexNode) }

                // Extend and update *current* edge length to account for
                // removed edge.
                edge.add(nextEdge, { inPlace: true })

                // Recurse required to check if edges either side of edge just
                // removed are also in same direction.
                return this.dissolveVertices()
            }
        }
    }

    /**
     * Rotate current polygon instance by an angle in *radians* around an origin
     * point. By default performs operation *in place*.
     * @param {number} angle Angle of rotation in *radians*.
     * @param {object} obj Optional destructured parameters object.
     * @param {Vector2D} [obj.origin] Origin of rotation, note that:
     * - If unset, defaults to the average point of each vertex in the current
     * polygon instance.
     * - Default calculated origin is equivalent to the average vertex of the
     * mid points of each edge.
     * - Default calculated origin is a good approximate visual center of the
     * polygon (it is a better apparent visual center than the center of the
     * polygon bounding box).
     * @param {boolean} [obj.inPlace] Should method update current instance of
     * polygon *in place*, or should method return a new polygon instance?
     * @returns {Polygon} Rotated polygon.
     */
    rotate(angle, { origin, inPlace = true } = {}) {
        const polygon = inPlace ? this : this.copy()

        // If no origin is provided in arguments, set origin of rotation to be
        // the *average* of all vertices in the polygon.
        if (!origin) {
            const averageVertex = new Vector2D(0, 0)
            for (const { value: vertex } of polygon.vertices) {
                averageVertex.add(vertex, { inPlace: true })
            }
            averageVertex.scalarDivide(polygon.vertices.size, { inPlace: true })
            origin = averageVertex
        }

        // Rotate each edge in the polygon instance, which will also rotate the
        // start vertex of each edge as a side effect.
        for (const { value: edge } of polygon.edges) {
            edge.rotate(angle, { origin, inPlace: true })
        }

        // If polygon is not closed, the last vertex will not have been rotated
        // by any of the edge rotations, so will need individually rotated.
        if (!polygon.isClosed) {
            polygon.vertices.tail?.value.rotate(angle, {
                origin,
                inPlace: true
            })
        }

        // Update internal rotation angle.
        polygon.#rotation = (polygon.#rotation + angle) % (2 * Math.PI)

        return polygon
    }

    /**
     * Translate current polygon instance by an `x` and `y` distance inferred
     * from the passed translation vector. By default performs operation *in
     * place*.
     * @param {Vector2D} vector Translation vector.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.inPlace] Should method update current instance of
     * polygon *in place*, or should method return a new polygon instance?
     * @returns {Polygon} Translated polygon.
     */
    translate(vector, { inPlace = true } = {}) {
        const polygon = inPlace ? this : this.copy()

        for (const { value: vertex } of polygon.vertices) {
            vertex.add(vector, { inPlace: true })
        }

        return polygon
    }

    /**
     * Split polygon edge with new vertex at a given fractional point along the
     * total length of the existing edge. Note that this method will:
     * - Add a new vertex on the existing edge.
     * - Update existing edge to end at new vertex.
     * - Add new edge between new vertex and end vertex of existing edge.
     * - Optionally add duplicate new vertex and split the internal vertex and
     * edge linked lists between these points.
     * @param {Edge} edge Polygon edge to split.
     * @param {object} obj Optional destructured parameters object.
     * @param {Vector2D} [obj.vertex] Vertex at which to split edge.
     * @param {number} [obj.fraction] Fraction of edge length to split at along
     * existing edge.
     * @param {boolean} [obj.splitPolygon] Should split edge operation also
     * split the internal linked list of edges and vertices?
     * @returns {?Polygon} Orphaned polygon fragment resulting from operation.
     * If no part of the polygon is orphaned (i.e. if current polygon instance
     * is closed), return `null` value.
     * @throws {PolygonError} Throws when:
     * - Supplied split vertex does not lie on the edge.
     * - Supplied fraction is not between 0 and 1 *exclusive*.
     * - Edge node with given value is not found in internal edge linked list.
     */
    splitEdge(edge, {
        vertex,
        fraction = 0.5,
        splitPolygon = false,
    } = {}) {
        // Throw if fraction is not a decimal between 0 and 1 *exclusive*.
        if (fraction <= 0 || fraction >= 1) {
            throw new PolygonError({
                message: "Split fraction invalid",
                fix: "Supply a split fraction between 0 and 1 EXCLUSIVE",
                fraction
            })
        }

        // Fetch edge node by value. Throw if node does not resolve to a node
        // contained in the polygon edges linked list.
        const node = this.edges.nodeWithValue(edge)
        if (!node) {
            throw new PolygonError({
                message: "Edge node with given value not found in polygon",
                fix: "Split edge which exists in polygon"
            })
        }
        const edgeNode = node
        const index = this.edges.indexOfNode(edgeNode)

        // Generate split vertex and correctly sized new edge node for split.

        // Generate split vertex if required from split fraction, and check that
        // split vertex is on edge. Throw if vertex not on edge.
        vertex ??= edge.start.add(edge.scalarMultiply(fraction))
        if (!edge.intersects(vertex)) {
            throw new PolygonError({
                message: "Split vertex does not lie on edge",
                fix: "Provide vertex which lies on edge being split"
            })
        }

        const vertexNode = new DoubleLinkedListNode(vertex)
        const edgeSegment = new Edge(vertex, edge.end)
        const edgeSegmentNode = new DoubleLinkedListNode(edgeSegment)

        // Update size of existing edge segment.
        edge.set(vertex.subtract(edge.start))

        // Add new vertex and edge nodes.
        this.vertices.addNode(vertexNode, { parentNode: index })
        this.edges.addNode(edgeSegmentNode, { parentNode: index })

        // If split polygon mode enabled, add a duplicate copy of split vertex
        // node, and split internal vertex and edge linked lists between these
        // nodes in order to split the polygon. Return orphaned polygon if
        // required.
        if (splitPolygon) {
            // Add duplicate copy of split vertex.
            this.vertices.addNode(
                new DoubleLinkedListNode(vertex.copy()),
                { parentNode: index }
            )

            // Split polygon between the two edge split vertices.
            const vertices = this.vertices.splitAtNode(index + 1)
            const edges = this.edges.splitAtNode(index)

            if (edges && vertices) { return Polygon.from(vertices, edges) }
        }

        return null
    }

    /**
     * Subtract an *external* source edge from the current polygon instance.
     * Note that this operation will:
     * - Modify any edge in the current polygon instance which is parallel with,
     * and intersects the source edge such that the existing edge no longer
     * overlaps any part of the source edge.
     * - Modify any edge which intersect the source edge at one point by
     * splitting the edge with *two* duplicate vertices. Also split internal
     * vertex and edge linked lists at this point.
     * - Split internal vertex and edge linked lists as required, updating
     * `head` and `tail` nodes in the process.
     * - Recurse when any split of an internal linked list is made, or when part
     * of the polygon is orphaned.
     * - Return any orphaned segments of the root polygon in an array of new
     * polygon instances.
     * @param {Edge} sourceEdge Source edge to subtract from current polygon
     * instance.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.filterRoot] Should filter current polygon instance
     * from returned array of polygons?
     * @param {boolean} [obj.isRoot] Is current instance the root instance to
     * be filtered?
     * @param {boolean} [obj.includeSourceVertices] Should start and end
     * vertices of the *source* edge be considered when calculating the
     * subtraction? If set to true, if source edge only intersects target edge
     * at one vertex, the target edge will still be split. Defaults to false.
     * @returns {Polygon[]} - Array of polygons resulting from edge subtraction.
     */
    subtractEdge(sourceEdge, {
        filterRoot = true,
        isRoot = true,
        includeSourceVertices = false
    } = {}) {
        // Note that when the edge subtraction process causes an update:
        // 1. The internal vertex and edge linked lists will be split.
        // 2. This will update the `head` and `tail` node references of those
        //    lists.
        // 3. As such any iterator consumer loop consuming internal linked lists
        //    must be reset by recursing this method.
        // 4. Upon recursion the same edge will not trigger a second update
        //    since it has already been updated to not overlap source edge.

        // Attempt to subtract source edge from every edge in current polygon
        // instance, recursing whenever the subtraction causes an update to
        // any edge in the polygon, or orphans a section of the polygon.
        for (const { value: targetEdge } of this.edges) {
            const polygon = this.#subtractEdgeFromTarget(
                targetEdge,
                sourceEdge,
                { includeSourceVertices }
            )

            // If edge subtraction causes update to an existing polygon edge
            // *and* produces an orphaned polygon instance, recurse with both
            // current instance and orphaned instance.
            if (polygon instanceof Polygon) {
                // Recurse subtract method with isRoot property set as required.
                // Note that since both the current and orphaned split polygon
                // instances contain the start and/or end vertices of the source
                // edge, the includeSourceVertices property is left to default
                // to `false` in order to prevent infinite recursion.
                const recursedInstance = this
                    .subtractEdge(sourceEdge, { filterRoot, isRoot })
                const recursedOrphan = polygon
                    .subtractEdge(sourceEdge, { filterRoot, isRoot: false })

                return [...recursedInstance, ...recursedOrphan]
            }

            // If edge subtraction *only* causes update to an existing polygon
            // edge , recurse with current instance.
            if (polygon === null) {
                return this.subtractEdge(sourceEdge, {
                    filterRoot,
                    isRoot,
                    includeSourceVertices
                })
            }
        }

        return filterRoot && isRoot ? [] : [this]
    }

    /**
     * Calculate *internal* angle at a given vertex using angle between
     * vertices and polygon curve orientation in order to determine if the
     * clockwise or anticlockwise angle between vectors at a vertex should be
     * used.
     * @param {Vector2D} vertex Vertex of required interior angle.
     * @returns {number} Internal angle at vertex measured in radians.
     * @throws {PolygonError} Throws when:
     * - Polygon is not closed. Vertex orientation of an incomplete polygon
     * cannot be determined, and therefore we cannot infer if the internal angle
     * at a vertex is given be clockwise or anticlockwise angle.
     * - Previous or next edge connecting to given vertex is not found.
     */
    internalAngleAtVertex(vertex) {
        // Throw if polygon not closed.
        if (!this.isClosed) {
            throw new PolygonError({
                message: "Polygon not closed, cannot calculate angle",
                fix: "Request angle at a vertex in a closed polygon"
            })
        }

        // Throw if edge node not found ending with given vertex.
        const edgeNode = this.edges.findNode(node => {
            return node.value.start === vertex
        })
        if (!edgeNode) {
            throw new PolygonError({
                message: "Edge node starting with vertex not found in polygon",
                fix: "Request angle at a vertex which exists in polygon"
            })
        }

        // Calculate anticlockwise angle at the vertex between the previous and
        // next edges connecting to the vertex. This value can then be used to
        // calculate return value based on if the internal angle at the vertex
        // is given by the clockwise or anticlockwise angle between edges.
        const {
            value: nextEdge,
            previous: { value: previousEdge }
        } = /** @type {DoubleLinkedNode.<Edge>}*/ (edgeNode)
        const anticlockwiseAngle =
            (Math.PI - nextEdge.direction + previousEdge.absoluteDirection) %
            (2 * Math.PI)
        const vertexOrientation = this.vertexOrientation

        return vertexOrientation === 1 ? anticlockwiseAngle
            : vertexOrientation === - 1 ? 2 * Math.PI - anticlockwiseAngle
            : 0
    }

    /**
     * Calculate *external* angle at a given vertex using angle between
     * vertices and polygon curve orientation in order to determine if the
     * clockwise or anticlockwise angle between vectors at a vertex should be
     * used.
     * @param {*} vertex Vertex of required exterior angle.
     * @returns {number} External angle at vertex measured in radians.
     * @throws {PolygonError} See {@link Polygon.internalAngleAtVertex here} for
     * information on when this method will throw based on the method it calls.
     * @see {@link Polygon#internalAngleAtVertex}
     */
    externalAngleAtVertex(vertex) {
        return Math.PI * 2 - this.internalAngleAtVertex(vertex)
    }

    /**
     * Check if a given point is inside the current polygon instance using ray
     * tracing method.
     * @param {Vector2D} vector Vector of point to check.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.includeEdges] Should points on polygon edges be
     * considered as enclosed by current polygon instance?
     * @returns {boolean} Does current polygon instance enclose given point?
     */
    enclosesPoint(vector, { includeEdges = true } = {}) {
        // Set initial flag to false, indicating that point is *not* enclosed
        // by polygon.
        let inside = false

        // Cast an imaginary *horizontal* ray to the *left* of the point vector.
        // Toggle `inside` flag every time ray crosses an edge. This is *not*
        // detected using the edge intersect method, since it causes issues when
        // the ray cast edge intersects vertices tangentially without entering
        // polygon. Instead this method checks if point is *right* of line, and
        // infers if ray cast has intersected edge based on this.
        for (const { value: edge } of this.edges) {
            // If requested point on edge, return immediately based on options.
            if (edge.intersects(vector)) { return includeEdges }

            // Get references of start and end vectors of edge, flipping as
            // required to ensure that the start vertex *always* has the smaller
            // `y` coordinate.
            const { start, end } = edge.start.y > edge.end.y
                ? { start: edge.end, end: edge.start }
                : edge

            // Detect if point lies between start and end vertex along the
            // y-axis, then infer if point is to the *right* of edge using
            // point orientations.
            const betweenEdgeVertices = vector.y > start.y !== vector.y > end.y
            const rightOfEdge = Vector2D.orientation(start, vector, end) === 1
            if (betweenEdgeVertices && rightOfEdge) { inside = !inside }
        }

        return inside
    }

    /**
     * Check if a given polygon is *entirely* enclosed by the current polygon
     * instance.
     * @param {Polygon} polygon Polygon to check if enclosed by current polygon
     * instance.
     * @returns {boolean} Does current polygon instance *entirely* enclose the
     * given polygon?
     */
    enclosesPolygon(polygon) {
        // Check that no edges intersect between the two instances.
        for (const { value: polygonEdge } of polygon.edges) {
            for (const { value: instanceEdge } of this.edges) {
                if (polygonEdge.intersects(instanceEdge)) {
                    return false
                }
            }
        }

        // Check that current instances encloses *all* vertices of polygon arg.
        for (const { value: polygonVertex } of polygon.vertices) {
            if (!this.enclosesPoint(polygonVertex, { includeEdges: false })) {
                return false
            }
        }

        return this.area > polygon.area
    }

    /**
     * Copy current polygon instance, returning a new instance containing copies
     * of all of the same vertices and edges such that no object references are
     * shared/duplicated between the two instances.
     * @returns {Polygon} New polygon instance copied from current instance.
     */
    copy() {
        // Make copies of all vertex and edge node *values*, ensuring also that
        // edge copies have the correct copied start vertex reference.
        const [vertices, edges] = /** @type {[Vector2D[], Edge[]]} */ ([[], []])
        for (const [index, vertex] of this.vertices.array.entries()) {
            const edge = this.edges.array[index]
            const vertexCopy = vertex.copy()
            vertices.push(vertexCopy)
            edges.push(edge.copy(vertexCopy))
        }

        // Construct new polygon from copied vertex and edge data arrays.
        const { isClosed } = this
        const polygon = Polygon.from(
            new DoubleLinkedList(vertices, { isClosed }),
            new DoubleLinkedList(edges, { isClosed }),
            { isLocked: this.#isLocked, rotation: this.#rotation }
        )

        return polygon
    }

    /**
     * Get a flattened array of all polygon vertices as [x,y] pairs.
     * @returns {number[]} Flat array of polygon vertices.
     */
    toFlatArray() {
        return this.vertices.array.flatMap(({ x, y }) => [x, y])
    }

    /**
     * Serialize flattened array of all polygon vertices.
     * @returns {string} Serialized polygon.
     */
    serialize() {
        return btoa(JSON.stringify(this.toFlatArray()))
    }

    /**
     * Subtract a given source edge from a target edge which exists on the
     * current polygon instance.
     * - Target edge *must* be known to exist when calling this private method.
     * - If edges intersect but do *not* overlap (i.e. are not parallel), split
     * target edge at intersection with *two* duplicate vertices, and split
     * internal vertex and edge linked lists at this point (note that this will
     * also update `head` and `tail` node references of these lists).
     * - If edges overlap (i.e. intersect and are parallel), return call to
     * private parallel-specific edge subtraction method.
     * @param {Edge} targetEdge Target edge of subtract operation which *must*
     * be known to exist in edge linked list of current polygon instance.
     * @param {Edge} sourceEdge Source edge to subtract from target edge.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.includeSourceVertices] Should start and end
     * vertices of the *source* edge be considered when calculating the
     * subtraction? If set to true, if source edge only intersects target edge
     * at one vertex, the target edge will still be split. Defaults to false.
     * @returns {Polygon|undefined|null} This method will return based on result
     * of subtraction operation:
     * - New polygon instance if subtraction orphans a part of the polygon.
     * - Value `null` if target edge is modified, but split does *not* orphan a
     * part of the polygon.
     * - Undefined if edges do *not* overlap or intersect, and no change is made
     * to target edge.
     */
    #subtractEdgeFromTarget(targetEdge, sourceEdge, {
        includeSourceVertices = false
    } = {}) {
        // Ignore if edges do not intersect.
        if (!sourceEdge.intersects(targetEdge)) { return }

        // If edges intersect and are parallel, complete edge subtraction using
        // secondary private method specifically for overlapping edges.
        if (sourceEdge.isParallelTo(targetEdge)) {
            return this.#subtractParallelEdgeFromTarget(targetEdge, sourceEdge)
        }

        // Get fractional intersection points between target edge and source
        // edge, and vice versa.
        const targetFraction = targetEdge.getIntersectionFraction(sourceEdge)
        const sourceFraction = sourceEdge.getIntersectionFraction(targetEdge)
        const splitVertex = targetEdge.getIntersection(sourceEdge)

        // Ignore if edges do not intersect, or if the *target* intersection
        // point is at the very start or end vertices of the target edge.
        if (targetFraction === null || sourceFraction === null) { return }
        if (targetFraction <= 0 || targetFraction >= 1) { return }

        // Depending on if the source edge vertices should be considered in the
        // subtraction operation, ignore either if source intersection is at the
        // start or end vertices of the source edge, *OR* if the source
        // intersection is beyond the start or end vertices of the source edge.
        if (includeSourceVertices) {
            if (sourceFraction < 0 || sourceFraction > 1) { return }
        }
        else { if (sourceFraction <= 0 || sourceFraction >= 1) { return } }

        return this.splitEdge(targetEdge, {
            vertex: splitVertex || undefined,
            splitPolygon: true,
        })
    }

    /**
     * Subtract a given source edge from a target edge which exists on the
     * current polygon instance, and is known to be *parallel* to the given
     * target edge.
     * - Target edge *must* be known to exist when calling this private method.
     * - Target edge and source edge *must* be known to be parallel.
     * - Parallel-specific subtraction operation will remove a portion of the
     * target edge, and split internal vertex and edge linked lists. As such,
     * `head` and `tail` node references may change.
     * @param {Edge} targetEdge Target edge of subtract operation which *must*
     * be known to exist in edge linked list of current polygon instance.
     * @param {Edge} sourceEdge Source edge to subtract from target edge.
     * @returns {Polygon|undefined|null} This method will return based on result
     * of parallel subtraction operation:
     * - New polygon instance if subtraction orphans a part of the polygon.
     * - Value `null` if target edge is modified, but split does *not* orphan a
     * part of the polygon.
     * - Undefined if edges do *not* overlap, and no change is made to target
     * edge (i.e. if edge only overlap an the start or end vertices).
     */
    #subtractParallelEdgeFromTarget(targetEdge, sourceEdge) {
        // Fetch index of node with given value of target Edge. Note that since
        // this is a private method which is called only with edges which are
        // known to exist on the current polygon instance, the index is assumed
        // to be non-negative, and no error check is required.
        const index = this.edges.indexOfValue(targetEdge)

        // Get start and end intersection/overlap points between the source edge
        // and the target edge as a fractional length along the target edge with
        // respect to the start vertex.
        const intersections = targetEdge
            .getParallelIntersectionFractions(sourceEdge)
        if (!intersections) { return }
        const { start: startFraction, end: endFraction } = intersections

        // Depending on where edges overlap, split the target edge. Remove any
        // overlapping parts and track the edges and vertices which are
        // orphaned due to this operation.
        let edges, vertices
        if (startFraction <= 0 && endFraction === 0) { return }
        else if (startFraction === 1 && endFraction >= 1) { return }
        else if (startFraction <= 0 && endFraction >= 1) {
            edges = this.edges.splitAtNode(index - 1, { deletionCount: 1 })
            vertices = this.vertices.splitAtNode(index)
        }
        else if (startFraction <= 0) {
            this.splitEdge(targetEdge, { fraction: endFraction })

            edges = this.edges.splitAtNode(index - 1, { deletionCount: 1 })
            vertices = this.vertices.splitAtNode(index)
        }
        else if (endFraction >= 1) {
            this.splitEdge(targetEdge, { fraction: startFraction })

            edges = this.edges.splitAtNode(index, { deletionCount: 1 })
            vertices = this.vertices.splitAtNode(index + 1)
        }
        else {
            this.splitEdge(targetEdge, { fraction: endFraction })
            this.splitEdge(targetEdge, {
                fraction: startFraction * (1 / endFraction)
            })

            edges = this.edges.splitAtNode(index, { deletionCount: 1 })
            vertices = this.vertices.splitAtNode(index + 1)
        }

        // If edges *and* vertices defined in the steps above, then return a
        // new polygon instance containing the orphaned polygon.
        if (edges && vertices) { return Polygon.from(vertices, edges) }

        // Heal internal vertices if orphaned vertices are swapped incorrectly.
        // This occurs when 1 vertex *should* be orphaned, and the split
        // operation above will instead orphan 0 edges, and orphan *all* of the
        // remaining vertices.
        if (!edges && vertices && this.vertices.size <= 1) {
            this.vertices = vertices
        }

        return null
    }

    /**
     * Generate new polygon instance from vertex and edge linked list instances
     * and other optional class properties.
     * @param {DoubleLinkedList.<Vector2D>} vertices Linked list of vertices to
     * generate polygon with.
     * @param {DoubleLinkedList.<Edge>} edges Linked list of edges to generate
     * polygon with.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.isLocked] Is new instance locked?
     * @param {number} [obj.rotation] Starting rotation of new instance.
     * @returns {Polygon} New polygon instance with given vertex and edge
     * linked lists set.
     */
    static from(vertices, edges, { isLocked = false, rotation = 0 } = {}) {
        // Create new polygon instance from given vertex and edge linked lists.
        const polygon = new Polygon()
        polygon.vertices = vertices
        polygon.edges = edges

        // Update optional private properties of new instance.
        polygon.isLocked = isLocked
        polygon.rotation = rotation

        return polygon
    }

    /**
     * Current rotation of polygon in *radians*.
     */
    get rotation() {
        return this.#rotation % (2 * Math.PI)
    }

    /**
     * Set current rotation of polygon in *radians*.
     */
    set rotation(rotation) {
        this.rotate((rotation - this.#rotation) % (2 * Math.PI))
    }

    /**
     * All polygon edges which are aligned with the horizontal axis (i.e.
     * parallel to the line y = 0).
     */
    get horizontalEdges() {
        return this.edges.array.filter(edge => edge.isHorizontal)
    }

    /**
     * All polygon edges which are aligned with the vertical axis (i.e. parallel
     * to the line x = 0).
     */
    get verticalEdges() {
        return this.edges.array.filter(edge => edge.isVertical)
    }

    /**
     * Is polygon a closed shape (i.e. do polygon edges form a closed loop)?
     */
    get isClosed() {
        return this.vertices.isClosed && this.edges.isClosed
    }

    /**
     * Is polygon locked (i.e. vertices and edges can no longer be updated)?
     * This property can *only* be set by internal class methods.
     */
    get isLocked() {
        return this.#isLocked
    }

    /**
     * Set internal isLocked flag, protected for setting only by this class or
     * extended classes.
     * @protected
     */
    set isLocked(boolean) {
        this.#isLocked = boolean
    }

    /**
     * Anchor, width, and height of bounding box fully containing polygon.
     */
    get boundingBox() {
        // Initiate corners at infinity points.
        const topLeftCorner = { x: Infinity, y: Infinity }
        const botRightCorner = { x: - Infinity, y: - Infinity }

        // Loop over all vertices, replacing corner coordinate components when
        // a smaller one is found. Note that `x` and `y` properties of each
        // corner should be updated separately to find absolute min/max
        // coordinate in every direction.
        for (const { value: vertex } of this.vertices) {
            if (vertex.x < topLeftCorner.x) { topLeftCorner.x = vertex.x }
            if (vertex.y < topLeftCorner.y) { topLeftCorner.y = vertex.y }
            if (vertex.x > botRightCorner.x) { botRightCorner.x = vertex.x }
            if (vertex.y > botRightCorner.y) { botRightCorner.y = vertex.y }
        }

        // Generate and return bounding box object.
        return {
            anchor: new Vector2D(topLeftCorner.x, topLeftCorner.y),
            width: botRightCorner.x - topLeftCorner.x,
            height: botRightCorner.y - topLeftCorner.y
        }
    }

    /**
     * Calculate perimeter of polygon by summing all edge magnitudes.
     */
    get perimeter() {
        if (!this.isClosed) { return NaN }

        let perimeter = 0
        for (const { value: edge } of this.edges) {
            perimeter += edge.magnitude
        }

        return perimeter
    }

    /**
     * Calculate signed area of polygon using the vectors of each vertex. See
     * [here](https://mathworld.wolfram.com/PolygonArea.html) for more
     * information on the algorithm. Note that:
     * - A `+ve` signed area indicates that points in the polygon are arranged
     * in a counterclockwise order.
     * - A `-ve` signed area indicates that points in the polygon are arranged
     * in a clockwise order.
     */
    get signedArea() {
        if (!this.isClosed) { return NaN }

        let area = 0
        for (const vertexNode of this.vertices) {
            const {
                value: vertex,
                next: { value: nextVertex }
            } = /** @type {DoubleLinkedNode.<Vector2D>} */ (vertexNode)
            area += vertex.x * nextVertex.y
            area -= nextVertex.x * vertex.y
        }
        area *= 0.5

        return area
    }

    /**
     * Absolute area of polygon inferred from signed area.
     */
    get area() {
        return Math.abs(this.signedArea)
    }

    /**
     * Infer curve orientation of polygon vertices given the signed area of the
     * polygon. See [here](https://en.wikipedia.org/wiki/Curve_orientation) for
     * more information on curve orientation.
     */
    get vertexOrientation() {
        return Math.sign(this.signedArea)
    }
}

/**
 * Named polygon error.
 */
class PolygonError extends DecoratedError {
    /**
     * Construct polygon named error.
     * @param {Object.<string, string|number|boolean>} options Options object
     * containing any string keys, and string, number or boolean values.
     */
    constructor(options) {
        super(options)
        this.name ??= "PolygonError"
    }
}

// @@exports
export { Polygon }
